/**
 * Searchable dropdown – filter options by typing. Used for nationality etc.
 */
import { useState, useRef, useEffect } from 'react';
import styles from './SearchableSelect.module.css';

export function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Search…',
  id,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const containerRef = useRef(null);

  const normalizedQuery = (query || '').trim().toLowerCase();
  const filtered =
    normalizedQuery === ''
      ? options
      : options.filter((opt) => opt.toLowerCase().includes(normalizedQuery));

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function handleSelect(opt) {
    onChange(opt);
    setQuery(opt);
    setOpen(false);
  }

  return (
    <div className={styles.wrap} ref={containerRef}>
      <div className={styles.inputWrap}>
        <input
          id={id}
          type="text"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-autocomplete="list"
          role="combobox"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={styles.input}
          autoComplete="off"
        />
        <span className={styles.chevron} aria-hidden>▼</span>
      </div>
      {open && (
        <ul className={styles.list} role="listbox">
          {filtered.length === 0 ? (
            <li className={styles.empty}>No matches</li>
          ) : (
            filtered.slice(0, 150).map((opt) => (
              <li
                key={opt}
                role="option"
                aria-selected={value === opt}
                className={value === opt ? styles.optionSelected : styles.option}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                {opt}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
